import { AxiosRequestConfig } from 'axios';

import { IActiveLoan, IMyLoan, ILoanRequest, ILoanRequestDetail, ILoanStatistics, TActiveLoans, TLoanSortParams, TLoanSortParamsWithAddress, ILoanCollectionWithPagination, IMoralisNFT, ICollectionOffers, INFTBestTrait } from '@/redux/loans/model';
import { SIMPLEHASH_CHAIN, SIMPLEHASH_CHAIN_NUMBER } from 'constant';
import { getWalletNFTs } from 'simplehash_api/walletApi';
import { AddressType } from 'types';

import { IAcceptLoanRequestPayload, IAnalytics, ICollectionCoinGecko, ICollectionLoanDetails, ICollectionSimplehashDetails, ILeaderBoard, ILoanRequestPayload, ILoanTermBaseFields, IPayloadUpdateLoanMember, IResponseCollectionDetails, IResponseNFT, ISignatureData, ISimplehashNFTDetails } from './model';
import { deleteRequest, getRequest, postRequest, putRequest } from '../api';

export const postEscrow = async (signatureData: ISignatureData): Promise<string> => {
  const res = await postRequest('/contracts/escrow', signatureData, undefined);
  return res.signature;
};

const postRequestBackend = (url: string, body: any, config?: AxiosRequestConfig) => {
  return postRequest(url, body, config);
};

export const getRequestBackend = (url: string, config?: AxiosRequestConfig) => {
  return getRequest(url, config);
};

export const makeLoanOffer = async (loanOfferPayload: ILoanRequestPayload) => {
  return postRequestBackend('/loan/request', loanOfferPayload);
};

type SimplifiedData = { nextCursors: { next: string; token_addresses: string[] }[]; nfts: IResponseNFT['result'] };
type fnReturnType<T> = T extends string | null ? IResponseNFT : SimplifiedData;

export const getNFTsInWallet = async <T extends string | string[] | null>(walletAddress: AddressType, next: string | null, contract_ids?: T, order_by?: string, limit?: number, collectionIds?: string, includeRarity?: boolean): Promise<fnReturnType<T>> => {
  if (typeof contract_ids === 'object' && contract_ids) {
    const chunks = [];

    for (let i = 0; i < contract_ids.length; i += 50) {
      chunks.push(contract_ids.slice(i, i + 50));
    }

    const cols = await Promise.all(
      chunks.map(async (contractArr) => {
        const params = {
          wallet_addresses: walletAddress,
          token_addresses: contractArr,
          order_by: order_by || 'last_sale_price__desc',
          ...(collectionIds ? { collection_ids: collectionIds } : {}),
          ...(next ? { cursor: next } : {}),
          ...(includeRarity ? { includeRarity } : {}),
        };

        return { result: await getWalletNFTs(params), params };
      })
    );

    const data = cols.reduce<SimplifiedData>(
      (agg, nftData) => {
        const nextCursors = [...agg.nextCursors];

        if (nftData.result.cursor) {
          nextCursors.push({ next: nftData.result.cursor, token_addresses: nftData.params.token_addresses });
        }
        const nfts = {
          nextCursors,
          nfts: [...agg.nfts, ...nftData.result.result],
        };
        return nfts;
      },
      { nfts: [], nextCursors: [] }
    );

    data.nfts.sort((a, b) => {
      const aPrice = Number(a.floor_price);
      const bPrice = Number(b.floor_price);
      return (bPrice || 0) - (aPrice || 0);
    });

    return data as fnReturnType<T>;
  }

  return getWalletNFTs({
    wallet_addresses: walletAddress,
    ...(contract_ids && { token_addresses: [contract_ids] }),
    order_by: order_by || 'last_sale_price__desc',
    limit: limit || 14,
    ...(collectionIds ? { collection_ids: collectionIds } : {}),
    ...(next ? { cursor: next } : {}),
  }) as unknown as fnReturnType<T>;
};

// export const getLoanCollectionsDetail = (contractAddress: string): Promise<IResponseCollection> => {
//   return postQuicknode('', {
//     id: 67,
//     jsonrpc: '2.0',
//     method: 'qn_fetchNFTCollectionDetails',
//     params: [
//       {
//         page: 1,
//         perPage: 80,
//         contracts: [contractAddress],
//       },
//     ],
//   });
// };

export const getLoanCollections = <T = ILoanCollectionWithPagination>(params: TLoanSortParams | undefined): Promise<T> => {
  return getRequestBackend('/loan/collections/', { params });
};

export const getLoanRequests = (config?: AxiosRequestConfig): Promise<ILoanRequest[]> => {
  return getRequestBackend('/loan/request', config);
};

export const getLoanRequestDetails = (id: string, config?: AxiosRequestConfig): Promise<ILoanRequestDetail> => {
  return getRequestBackend(`/loan/request/${id}`, config);
};

export const getActiveLoans = (config?: AxiosRequestConfig): Promise<TActiveLoans> => {
  return getRequestBackend('/loan/active-loans', config);
};

export const getActiveLoan = (id: string, config?: AxiosRequestConfig): Promise<IActiveLoan> => {
  return getRequestBackend(`/loan/active-loans/${id}`, config);
};

export const acceptLoanRequestByBackend = (body: IAcceptLoanRequestPayload): Promise<ILoanRequest[]> => {
  return postRequestBackend('/loan/accept-request', body);
};

export const deleteLoanRequest = (id: string) => {
  return deleteRequest(`/loan/request/${id}`);
};

export const updateLoanRequest = (id: string, body: ILoanTermBaseFields) => {
  return putRequest(`/loan/request/${id}`, body);
};

export const getLoanStatistics = async ({ address }: { address: AddressType }): Promise<ILoanStatistics> => {
  return getRequestBackend('/loan/statistics', {
    params: {
      address,
    },
  });
};

export const getLentLoans = async (params: TLoanSortParamsWithAddress): Promise<IMyLoan[]> => {
  return getRequestBackend('/loan/lent', {
    params,
  });
};

export const getCollectionOffers = async (collectionSlug: string): Promise<ICollectionOffers> => {
  return getRequestBackend('/simplehash/collection-offers', {
    params: {
      collectionSlug,
    },
  });
};

export const getBorrowedLoans = async (params: TLoanSortParams & { address: string }): Promise<IMyLoan[]> => {
  return getRequestBackend('/loan/borrowed', {
    params,
  });
};

export const getOffersLoans = async (params: TLoanSortParamsWithAddress): Promise<ILoanRequest[]> => {
  return getRequestBackend('/loan/my-loan-offers', {
    params,
  });
};

export const cancelLoanOffer = async (id: number) => {
  return deleteRequest(`/loan/offer/${id}`);
};

export const searchCollections = async (searchTerm: string): Promise<ILoanCollectionWithPagination> => {
  return getRequestBackend('/loan/collections/', {
    params: {
      search: searchTerm,
    },
  });
};

export const getCollectionDetailsByIds = (collectionAddress: string): Promise<ICollectionSimplehashDetails> => {
  return getRequestBackend('/simplehash/collections-by-id', {
    params: {
      collectionAddress,
    },
  });
};

export const getCollectionByAddressCoinGecko = ({ contractAddress, days }: { contractAddress: string; days?: number }): Promise<ICollectionCoinGecko> => {
  return getRequestBackend('/simplehash/collections-by-address-coingecko', {
    params: {
      contractAddress,
      days,
    },
  });
};

export const getCollectionDetailsByContract = (contractAddress: string): Promise<IResponseCollectionDetails> => {
  return getRequestBackend(`/simplehash/collections/${SIMPLEHASH_CHAIN_NUMBER}/${contractAddress}`, {});
};

export const getLoanCollectionDetails = async (id: string, params: any): Promise<ICollectionLoanDetails> => {
  return getRequestBackend(`/loan/collections/${id}`, { params });
};

export const getNFTDetails = async (contract: string, tokenId: number | string): Promise<ISimplehashNFTDetails> => {
  return getRequestBackend(`/simplehash/${SIMPLEHASH_CHAIN}/${contract}/${tokenId}`);
};

export const getMultipleNFTsDetails = async (tokens: { token_address: string; token_id: string }[]): Promise<IMoralisNFT[]> => {
  return postRequestBackend('/simplehash/multiple-nft-details', {
    tokens,
  });
};

export const updateLoanMemberAddress = async ({ id, address }: IPayloadUpdateLoanMember) => {
  return putRequest(`/loan/update-loan-member-address/${id}`, undefined, {
    params: {
      address,
    },
  });
};

export const getAnalytics = async (params: { dateRange?: string; currency?: string }): Promise<IAnalytics> => {
  return getRequestBackend('/analytics', {
    params,
  });
};

export const getLeaderBoard = async (params: { page: number; limit: number; sort: string; dir: string }): Promise<ILeaderBoard> => {
  return getRequestBackend('/points/leaderboard', {
    params,
  });
};

export const getBestTrait = async (contractAddress: string, tokenId: number | string): Promise<INFTBestTrait> => {
  const myHeaders = new Headers();
  myHeaders.append('accept', 'application/graphql-response+json, application/graphql+json, application/json, text/event-stream, multipart/mixed');
  myHeaders.append('accept-language', 'en-US,en;q=0.9');
  myHeaders.append('content-type', 'application/json');
  myHeaders.append('origin', 'https://opensea.io');
  myHeaders.append('priority', 'u=1, i');
  myHeaders.append('referer', 'https://opensea.io/');
  myHeaders.append('sec-ch-ua', '"Google Chrome";v="137", "Chromium";v="137", "Not/A)Brand";v="24"');
  myHeaders.append('sec-ch-ua-mobile', '?0');
  myHeaders.append('sec-ch-ua-platform', '"Linux"');
  myHeaders.append('sec-fetch-dest', 'empty');
  myHeaders.append('sec-fetch-mode', 'cors');
  myHeaders.append('sec-fetch-site', 'same-site');
  myHeaders.append('user-agent', 'Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/137.0.0.0 Safari/537.36');
  myHeaders.append('x-app-id', 'os2-web');
  myHeaders.append('x-query-signature', 'b33b853f93368c99af085e6379c2ca071908e2b3704410cd62770c011e355a4d');
  myHeaders.append('Cookie', '__cf_bm=YORmMzjqdtZTFKX7Zx9sko6bLSG8dka3ykteAgSfyyg-1750922798-1.0.1.1-.cuf0nennH6U7QzeOXdlRKMYgqTPcTCcJd516llY6hDYu3d87cTidUrpiC7lwMCln71_ENPesc.957_VDsj_GQHNAOXNYWj_f0sH3AIh9Ps; _cfuvid=iBdHd0q6CxUURhXMH8i2ah6QLlgq12dUx_t07GvdoCw-1750759650046-0.0.1.1-604800000');

  const graphql = JSON.stringify({
    query:
      'query ItemAttributesQuery($identifier: ItemIdentifierInput!) {\n  itemByIdentifier(identifier: $identifier) {\n    __typename\n    ... on Item {\n      id\n      attributes {\n        traitType\n        value\n        __typename\n      }\n      ...ItemAttributesGrid\n      ...ItemAttributesTable\n      __typename\n    }\n  }\n}\nfragment ItemAttributesGrid on Item {\n  id\n  collection {\n    id\n    slug\n    __typename\n  }\n  attributes {\n    traitType\n    value\n    floorPrice {\n      native {\n        unit\n        __typename\n      }\n      ...TokenPrice\n      __typename\n    }\n    stats {\n      itemCount\n      percent\n      __typename\n    }\n    __typename\n  }\n  __typename\n}\nfragment TokenPrice on Price {\n  usd\n  token {\n    unit\n    symbol\n    contractAddress\n    chain {\n      identifier\n      __typename\n    }\n    __typename\n  }\n  __typename\n}\nfragment ItemAttributesTable on Item {\n  attributes {\n    traitType\n    value\n    __typename\n  }\n  ...ItemAttributesTableRow\n  __typename\n}\nfragment ItemAttributesTableRow on Item {\n  attributes {\n    traitType\n    value\n    floorPrice {\n      native {\n        unit\n        __typename\n      }\n      ...TokenPrice\n      __typename\n    }\n    topOfferPrice {\n      native {\n        unit\n        __typename\n      }\n      ...TokenPrice\n      __typename\n    }\n    stats {\n      itemCount\n      percent\n      __typename\n    }\n    __typename\n  }\n  collection {\n    slug\n    __typename\n  }\n  __typename\n}',
    variables: { identifier: { chain: 'ethereum', contractAddress, tokenId } },
  });
  const requestOptions = {
    method: 'POST',
    headers: myHeaders,
    body: graphql,
  };

  const result: { data: { itemByIdentifier?: { attributes: { floorPrice: { native: { unit: number } } }[] } } } = await fetch('https://gql.opensea.io/graphql', requestOptions).then((response) => response.json());
  const maxTraitPrice = result.data.itemByIdentifier?.attributes.reduce((agg, item) => {
    if (item.floorPrice && item.floorPrice.native.unit > agg) {
      return item.floorPrice.native.unit;
    }
    return agg;
  }, 0);

  return { maxTraitPrice: maxTraitPrice || 0 };
};

export const getUserPoints = async ({ walletAddress }: { walletAddress: string }): Promise<{ totalPoints: number }> => {
  return getRequestBackend('/points/my-points', {
    params: {
      walletAddress,
    },
  });
};
